import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Guid } from 'guid-typescript';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { first, map, switchMap, tap } from 'rxjs/operators';
import { Property } from '../models/property';
import { User } from '../models/user';
import { OrganizationsService } from './organizations.service';
import { ServiceBase } from './service-base';
import { EnumReasonResetPassword } from '../models/enum-reason-reset-password';
import { EnumModeResetPassword } from '../models/enum-mode-reset-password';

@Injectable({
    providedIn: 'root',
})
export class UsersService extends ServiceBase {
    public currentUser: BehaviorSubject<User>;

    public get currentUserChange$() {
        return this.currentUser.asObservable();
    }

    constructor(private http: HttpClient, private organizationsServices: OrganizationsService) {
        super();
        this.currentUser = new BehaviorSubject<User>(JSON.parse(sessionStorage.getItem('user')));
    }

    public setSessionCurrentUser(user: User) {
        sessionStorage.setItem('user', JSON.stringify(user));
        this.currentUser.next(user);
    }

    public getSessionCurrentUser(): User {
        return this.currentUser.value;
    }

    hasRight(aclName: string): boolean {
        return JSON.parse(sessionStorage.getItem('user')).rights.filter((r) => r.name == aclName && r.value > 0).length > 0;
    }

    getCurrentUser(): Observable<User> {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.get<User>(`${this.baseUrl}/api/user`, httpOptions).pipe(
            tap((user: User) => {
                sessionStorage.setItem('user', JSON.stringify(user));
                this.currentUser.next(user);
            })
        );
    }

    public checkCurrentUser(accessToken: string): Observable<User> {
        const httpOptions = {
            headers: new HttpHeaders().set('Authorization', accessToken).set('X-OrganizationId', this.organizationsServices.getCurrentOrganization()?.id ?? ''),
        };

        return this.http.get<User>(`${this.baseUrl}/api/user`, httpOptions).pipe(
            tap((user: User) => {
                sessionStorage.setItem('user', JSON.stringify(user));
                sessionStorage.setItem('SessionId', Guid.create().toString());
                this.currentUser.next(user);
            })
        );
    }

    getUser(userId: string): Observable<User> {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.organizationsServices.getCurrentOrganization().id ?? ''),
        };

        return this.http.get<User>(`${this.baseUrl}/api/users/${userId}`, httpOptions).pipe(
            map((user) => {
                return user;
            })
        );
    }

    getUserWithDisplayParentOrganization(userId: string, executeOption: boolean = true): Observable<User> {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', !executeOption ? '' : this.organizationsServices.getCurrentOrganization().id ?? ''),
        };

        return this.http.get<User>(`${this.baseUrl}/api/users/${userId}`, httpOptions).pipe(
            switchMap((user) =>
                this.organizationsServices.getOrganisation(user.organizationId).pipe(
                    map((organization) => {
                        user.displayParentOrganization = organization.display;

                        return user;
                    })
                )
            )
        );
    }

    getUsersWithDisplayParentOrganization(): Observable<User[]> {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.organizationsServices.getCurrentOrganization().id ?? ''),
        };

        return forkJoin([this.http.get<User[]>(`${this.baseUrl}/api/users`, httpOptions), this.organizationsServices.getOrganisations()]).pipe(
            map((res) => {
                const users = res[0];
                const organizations = res[1];

                users.forEach((user) => {
                    const displayParentOrg = organizations.find((x) => x.id === user.organizationId);
                    user.displayParentOrganization = displayParentOrg ? displayParentOrg.display : '';
                });

                return users;
            })
        );
    }

    getUsers(): Observable<User[]> {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.organizationsServices.getCurrentOrganization().id ?? ''),
        };

        return this.http.get<User[]>(`${this.baseUrl}/api/users`, httpOptions);
    }

    addUser(user: User): Observable<User> {
        const httpOptions = {
            headers: new HttpHeaders().set('Content-Type', 'application/json').set('X-OrganizationId', this.organizationsServices.getCurrentOrganization().id ?? ''),
        };

        return this.http.post<User>(`${this.baseUrl}/api/users`, JSON.stringify(user), httpOptions);
    }

    deleteUser(userId: string) {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.organizationsServices.getCurrentOrganization().id ?? ''),
        };

        return this.http.delete(`${this.baseUrl}/api/users/${userId}`, httpOptions);
    }

    deleteUserProperty(userId: string, propertyName: string) {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.organizationsServices.getCurrentOrganization().id ?? ''),
        };

        return this.http.delete(`${this.baseUrl}/api/users/${userId}/properties/${propertyName}`, httpOptions);
    }

    deleteUserRight(userId: string, rightName: string) {
        const httpOptions = {
            headers: new HttpHeaders().set('X-OrganizationId', this.organizationsServices.getCurrentOrganization().id ?? ''),
        };

        return this.http.delete(`${this.baseUrl}/api/users/${userId}/rights/${rightName}`, httpOptions);
    }

    updateUser(user: User): Observable<User> {
        const httpOptions = {
            headers: new HttpHeaders().set('Content-Type', 'application/json').set('X-OrganizationId', this.organizationsServices.getCurrentOrganization().id ?? ''),
        };

        return this.http.patch<User>(`${this.baseUrl}/api/users/${user.id}`, user, httpOptions);
    }

    updateUserProperty(userId: string, propertyName: string, propertyValue: string): Observable<Property> {
        const httpOptions = {
            headers: new HttpHeaders().set('Content-Type', 'application/json').set('X-OrganizationId', this.organizationsServices.getCurrentOrganization().id ?? ''),
        };

        return this.http.put<Property>(`${this.baseUrl}/api/users/${userId}/properties/${propertyName}`, JSON.stringify(propertyValue), httpOptions);
    }

    updateUserRight(userId: string, rightName: string, rightValue: number): Observable<Property> {
        const httpOptions = {
            headers: new HttpHeaders().set('Content-Type', 'application/json').set('X-OrganizationId', this.organizationsServices.getCurrentOrganization().id ?? ''),
        };

        return this.http.put<Property>(`${this.baseUrl}/api/users/${userId}/rights/${rightName}`, rightValue, httpOptions);
    }

    removeCurrentUser() {
        sessionStorage.clear();

        this.currentUser.next(null);
        this.organizationsServices.removeCurrentOrganization();
    }

    StartPasswordReset(userId: string, reason: EnumReasonResetPassword, mode: EnumModeResetPassword, inputUrl: string, timeout: number = null): Observable<string | null> {
        const httpOptions = {
            headers: new HttpHeaders().set('Content-Type', 'application/json'),
            params: new HttpParams().set('reason', reason).set('mode', mode).set('inputUrl', inputUrl).set('timeout', timeout),
        };

        return this.http.post<string>(`${this.baseUrl}/api/users/${userId}/resetPassword`, null, httpOptions);
    }
}
